import { useState, useEffect } from 'react';
import { userService, User, CreateUserData, UpdateUserData } from '../../services/user.service';
import { useAuth } from '../../contexts/AuthContext';

const USER_ROLES = [
  { value: 'sys_admin', label: 'Administrador del Sistema' },
  { value: 'general_manager', label: 'Gerente General' },
  { value: 'project_lead', label: 'Líder de Proyecto' },
  { value: 'accountant', label: 'Contador' },
  { value: 'contractor', label: 'Contratista' },
  { value: 'receptionist', label: 'Recepcionista' },
  { value: 'data_entry', label: 'Capturista de Datos' },
  { value: 'client', label: 'Cliente' },
];

const INITIAL_FORM: CreateUserData = {
  email: '',
  name: '',
  password: '',
  role: 'client',
  phone: '',
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState<CreateUserData>(INITIAL_FORM);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await userService.getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (editingUser) {
        const updateData: UpdateUserData = {
          name: formData.name,
          role: formData.role,
          phone: formData.phone,
        };
        await userService.updateUser(editingUser.id, updateData);
      } else {
        await userService.createUser(formData);
      }
      await loadUsers();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar usuario');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres desactivar este usuario?')) return;
    try {
      await userService.deleteUser(id);
      await loadUsers();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar usuario');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await userService.toggleUserStatus(user.id, !user.isActive);
      await loadUsers();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      phone: user.phone || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(INITIAL_FORM);
    setError('');
  };

  const getRoleLabel = (role: string) => {
    return USER_ROLES.find(r => r.value === role)?.label || role;
  };

  const filteredUsers = users.filter(u => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term);
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && u.isActive) ||
      (filterStatus === 'inactive' && !u.isActive);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const activeFiltersCount =
    (filterRole !== 'all' ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Gestión de Personal</h1>
          <p className="subtitle">Administra los usuarios y sus roles en el sistema</p>
        </div>
        <div className="actions">
          <button onClick={loadUsers} className="btn btn-ghost">
            🔄 Actualizar
          </button>
          <button
            onClick={() => { setEditingUser(null); setFormData(INITIAL_FORM); setShowModal(true); }}
            className="btn btn-primary"
          >
            + Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Error global */}
      {error && !showModal && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-search">
          <span className="toolbar-search-icon">🔍</span>
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="form-select"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="all">Todos los roles</option>
            {USER_ROLES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>

          {activeFiltersCount > 0 && (
            <span className="filter-badge">
              {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''} activo{activeFiltersCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && users.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando usuarios...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        /* Empty state */
        <div className="empty-state">
          <div className="empty-state-icon">
            {users.length === 0 ? '👥' : '🔍'}
          </div>
          <h3 className="empty-state-title">
            {users.length === 0 ? 'No hay usuarios registrados' : 'Sin resultados'}
          </h3>
          <p className="empty-state-text">
            {users.length === 0
              ? 'Comienza creando el primer usuario del sistema.'
              : 'No se encontraron usuarios que coincidan con los filtros aplicados.'}
          </p>
          {users.length === 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary"
            >
              + Crear primer usuario
            </button>
          )}
        </div>
      ) : (
        /* Tabla */
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Teléfono</th>
                <th className="col-center">Estado</th>
                <th className="col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="user-avatar" style={{ width: '36px', height: '36px', fontSize: '16px' }}>
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{u.name}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                          ID: {u.id.slice(0, 8)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <span className="badge badge-neutral">{getRoleLabel(u.role)}</span>
                  </td>
                  <td>{u.phone || '—'}</td>
                  <td className="col-center">
                    <span className={`badge ${u.isActive ? 'badge-success' : 'badge-error'}`}>
                      {u.isActive ? '✅ Activo' : '⏸️ Inactivo'}
                    </span>
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons-group">
                      <button
                        onClick={() => handleEdit(u)}
                        className="btn-action-edit"
                        title="Editar usuario"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => handleToggleStatus(u)}
                        className="btn-action-toggle"
                        title={u.isActive ? 'Desactivar' : 'Activar'}
                      >
                        {u.isActive ? '⏸️' : '▶️'}
                      </button>
                      {u.id !== currentUser?.id && (
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="btn-action-delete"
                          title="Eliminar usuario"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}</h3>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Nombre completo</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: Juan Pérez"
                    />
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Correo electrónico</label>
                    <input
                      type="email"
                      className="form-input"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="usuario@email.com"
                      disabled={!!editingUser}
                    />
                    {editingUser && (
                      <span className="form-help">El email no se puede modificar</span>
                    )}
                  </div>

                  <div className="form-col">
                    <label className="form-label">Teléfono</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="81 1234 5678"
                    />
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Rol</label>
                    <select
                      className="form-select"
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      {USER_ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  {!editingUser && (
                    <div className="form-col">
                      <label className="form-label required">Contraseña temporal</label>
                      <input
                        type="password"
                        className="form-input"
                        required
                        minLength={8}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Mínimo 8 caracteres"
                      />
                      <span className="form-help">El usuario deberá cambiarla al primer inicio</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="info-panel error" style={{ marginTop: '16px' }}>
                    <span className="info-panel-icon">❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-ghost">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-primary">
                  {isSaving ? 'Guardando...' : editingUser ? 'Actualizar' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}